import { today } from '../../today';
import { IDiscountCampaign, IDiscountCampaigns } from './discount.interfaces';
import { DB_SYSTEM, DB_PARAMETERS, DB_DISCOUNT_CAMPAIGNS } from 'src/app/constants';
import { firestore } from 'firebase';
import { TBookAlias } from '@shared/book/interfaces';
import { BOOK_ALIAS_STARRY_DREAM, BOOK_ALIAS_WELCOME_TO_THE_WOLD, BOOK_ALIAS_WHOS_BIRTHDAY_TOMORROW } from '@shared/book/constants';
import { Logger } from '../logger.service';
import { ShippingMethod } from '@functions/interfaces';

export const getPercentFromValue = (
  v: number,
  p: number,
) => v / 100 * p;

export const removePercentFromValue = (
  v: number,
  p: number,
) => v - getPercentFromValue(v, p);

export const getDiscountCampaignsColRef = () => firestore()
  .collection(DB_SYSTEM)
  .doc(DB_PARAMETERS)
  .collection(DB_DISCOUNT_CAMPAIGNS) as firestore.CollectionReference<IDiscountCampaign>;

export const getActiveDiscountCampaign = (
  discountCampaigns: IDiscountCampaigns
) => {
  return Object.values(discountCampaigns).find(({ start, end }) => {
    if (!start || !end) {
      throw new Error('discountCampaign is invalid');
    }

    const startUnix = start.toMillis();
    const todayUnix = today().toDate().getTime();
    const endUnix = end.toMillis();

    return todayUnix > startUnix && todayUnix <= endUnix
  })
}

/**
 * @param price to apply discount on
 * @param discount could be percentOff: `50%` or amountOff: `200`
 * @returns updated price
 */
export const calculateDiscount = (
  price: number,
  discount: string,
) => {
  const isPercent = /\%$/g.test(discount);

  if (isPercent) {
    const percentOff = Number(discount.slice(0, discount.length - 1));

    if (isNaN(percentOff)) throw new Error('invalid percent ' + discount);

    price = removePercentFromValue(price, percentOff);
  } else {
    const amountOff = Number(discount);

    if (isNaN(amountOff)) throw new Error('invalid amount ' + discount);

    price = price - amountOff;
  }

  price = Math.round(price);

  return price < 0 ? 0 : price;
}

/**
 * @returns `alias specific discount` or `global discount` or `undefined`
 */
const getBooksDiscountLog = new Logger('getBooksDiscount');
export const getBooksDiscount = (
  discountCampaign: IDiscountCampaign,
  alias: TBookAlias,
): string => {
  switch (alias) {
    case BOOK_ALIAS_STARRY_DREAM:
      return discountCampaign.discountStarryDream || discountCampaign.discount;
    case BOOK_ALIAS_WELCOME_TO_THE_WOLD:
      return discountCampaign.discountWelcomeToTheWorld || discountCampaign.discount;
    case BOOK_ALIAS_WHOS_BIRTHDAY_TOMORROW:
      return discountCampaign.discountWhosBirthdayTomorrow || discountCampaign.discount;
    default:
      getBooksDiscountLog.error(`Book by alias '${alias}' not handled yet.`, {discountCampaign});
      return undefined;
  }
}

const getShippingDiscountLog = new Logger('getShippingDiscount');

/**
 * @returns `shipping specific discount` or `undefined`
 */
export const getShippingDiscount = (
  discountCampaign: IDiscountCampaign,
  shippingMethod: ShippingMethod,
): string | undefined => {
  const { shippingDiscount, premiumShippingDiscount } = discountCampaign;

  switch (shippingMethod) {
    case 'premiumShipping':
      return premiumShippingDiscount;
    case 'regularShipping':
      return shippingDiscount;
    default:
      getShippingDiscountLog.error(`getShippingDiscount: Shipping method '${shippingMethod}' not handled yet.`, {
        discountCampaign,
      });
      return undefined;
  }
}
